/**
 * Authentication Context
 * Manages user authentication state and operations
 */

import { AuthResponse, IUser, LoginCredentials } from '@hrms/shared/types';
import { AxiosError } from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import apiClient from '../api/client';
import { clearAccessToken, setAccessToken } from '../api/interceptors';
import { usersApi } from '../api/users.api';
import { AUTH_ERRORS } from '../constants';
import { socketService } from '../services/socket.service';
import { setStoredRoles } from '../utils/roleStorage';


interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: string;
  department?: string;
  designation?: string;
  phone?: string;
}

interface PasswordResetData {
  email: string;
}

interface ResetPasswordData {
  token: string;
  newPassword: string;
}

interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

interface AuthContextType {
  user: IUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  forgotPassword: (data: PasswordResetData) => Promise<void>;
  resetPassword: (data: ResetPasswordData) => Promise<void>;
  changePassword: (data: ChangePasswordData) => Promise<void>;
  setUser: (user: IUser) => void;
  clearError: () => void;
}

// Exported so tests can supply a controlled auth value without mounting the
// real AuthProvider (which performs network calls on mount).
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export type { AuthContextType };

const USER_CACHE_KEY = 'auth_user';
const saveUserCache = (u: IUser) => { try { sessionStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); } catch {} };
const loadUserCache = (): IUser | null => { try { const s = sessionStorage.getItem(USER_CACHE_KEY); return s ? JSON.parse(s) : null; } catch { return null; } };
const clearUserCache = () => { try { sessionStorage.removeItem(USER_CACHE_KEY); } catch {} };

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<IUser | null>(() => loadUserCache());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loginSucceededRef = useRef(false);
  const checkAuthInProgressRef = useRef(false);

  const setUserWithCache = useCallback((u: IUser) => {
    setUser(u);
    saveUserCache(u);
  }, []);

  const clearError = useCallback(() => setError(null), []);



  const handleApiError = (err: unknown): string => {
    const error = err as AxiosError<{
      message?: string;
      error?: string | { message?: string; details?: { field: string; message: string }[] };
    }>;
    const data = error.response?.data;
    if (!data) return AUTH_ERRORS.genericError;
    // Structured validation error: { error: { message, details } }
    if (data.error && typeof data.error === 'object') {
      const details = data.error.details;
      if (details?.length) {
        return details.map((d) => d.message).join(', ');
      }
      return data.error.message || data.message || AUTH_ERRORS.genericError;
    }
    // Simple message: { message } or { error: 'string' }
    return data.message || (typeof data.error === 'string' ? data.error : '') || AUTH_ERRORS.genericError;
  };

  const checkAuth = useCallback(async () => {
    // Prevent concurrent calls — React 18 Strict Mode double-fires useEffect,
    // which would race two refresh-token calls against a single-use token.
    if (checkAuthInProgressRef.current) return;
    checkAuthInProgressRef.current = true;
    try {
      // The httpOnly cookie is sent automatically (withCredentials: true).
      // If the cookie is missing or expired the request will 401 and we land in catch.
      const refreshRes = await apiClient.post<{ success: boolean; data: AuthResponse }>(
        '/auth/refresh-token'
      );
      const { accessToken } = refreshRes.data.data;
      setAccessToken(accessToken);

      // Don't overwrite user if login() already succeeded concurrently
      if (!loginSucceededRef.current) {
        // Always fetch the latest user from the server — cache is only a
        // fallback for offline/failure scenarios, never the primary source.
        // Otherwise admin-side profile updates never reach the employee's
        // browser until they log out and back in.
        const meRes = await apiClient.get('/auth/me');
        setUserWithCache(meRes.data.data.user);
        socketService.connect(accessToken);
        usersApi
          .getRoles()
          .then((roles) => {
            if (Array.isArray(roles) && roles.length > 0) {
              setStoredRoles(roles);
            }
          })
          .catch(() => {});
      }
    } catch {
      // Cookie absent or expired — user is not authenticated
      if (!loginSucceededRef.current) {
        clearAccessToken();
        clearUserCache();
        setUser(null);
        socketService.disconnect();
      }
    } finally {
      setIsLoading(false);
      checkAuthInProgressRef.current = false;
    }
  }, [setUserWithCache]);

  const login = async (credentials: LoginCredentials) => {
    try {
      setError(null);
      setIsLoading(true);
      const loginRes = await apiClient.post<{ success: boolean; data: AuthResponse }>(
        '/auth/login',
        credentials
      );
      const { accessToken } = loginRes.data.data;
      setAccessToken(accessToken);

      // Fetch the full populated user profile
      const meRes = await apiClient.get('/auth/me');
      setUserWithCache(meRes.data.data.user);

      usersApi
        .getRoles()
        .then((roles) => {
          if (Array.isArray(roles) && roles.length > 0) {
            setStoredRoles(roles);
          }
        })
        .catch(() => {});

      loginSucceededRef.current = true;
      try {
        sessionStorage.removeItem('hrms_dismissed_notifications');
        localStorage.removeItem('hrms_dismissed_notifications');
      } catch {}

      // Connect to Socket.io after successful login
      socketService.connect(accessToken);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setError(null);
      setIsLoading(true);
      await apiClient.post('/auth/register', data);
      // Registration successful - user needs to verify email
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Cookie is sent automatically; server reads it, revokes the token, clears the cookie
      await apiClient.post('/auth/logout');
    } catch (err) {
      console.warn('Logout API call failed:', err);
    } finally {
      clearAccessToken();
      clearUserCache();
      try {
        sessionStorage.removeItem('hrms_dismissed_notifications');
        localStorage.removeItem('hrms_dismissed_notifications');
      } catch {}
      setUser(null);
      socketService.disconnect();
    }
  };

  const verifyEmail = useCallback(async (token: string) => {
    try {
      setError(null);
      await apiClient.post('/auth/verify-email', { token });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const resendVerification = async (email: string) => {
    try {
      setError(null);
      await apiClient.post('/auth/resend-verification', { email });
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const forgotPassword = async (data: PasswordResetData) => {
    try {
      setError(null);
      await apiClient.post('/auth/forgot-password', data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const resetPassword = async (data: ResetPasswordData) => {
    try {
      setError(null);
      await apiClient.post('/auth/reset-password', data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const changePassword = async (data: ChangePasswordData) => {
    try {
      setError(null);
      await apiClient.post('/auth/change-password', data);
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  useEffect(() => {
    void checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        register,
        logout,
        checkAuth,
        verifyEmail,
        resendVerification,
        forgotPassword,
        resetPassword,
        changePassword,
        setUser: setUserWithCache,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};